import {
  Anchor,
  Box,
  Button,
  Center,
  Divider,
  Drawer,
  Group,
  Loader,
  Notification,
  NumberInput,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import React, { FC, useCallback, useEffect, useState } from "react";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { useForm } from "@mantine/form";
import { AiTaskStatus, JobRecommendTaskDTO } from "../models/Message";
import { AiSelectedJob, JobQuery } from "../models/AiSelectedJob";
import { MdCheck, MdClose } from "react-icons/md";
import { ChatForm } from "../models/ChatSession";
import { useDisclosure } from "@mantine/hooks";
import { AiJobRecommendResultResponse } from "../models/AiJobRecommendResultResponse";
import HistoricalJobRecommendationsModal from "./HistoricalJobRecommendationsModal";

interface JobRecommendationDrawerProps {
  drawerOpened: boolean;
  drawerHandlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  };
  chatSessionId: string | undefined;
  jobRecommendTaskStatusDto: JobRecommendTaskDTO | null;
  setJobRecommendTaskStatusDTO: (statusDto: JobRecommendTaskDTO) => void;
  chatForm: ChatForm;
  updateHasJobAlert: (chatSessionId: string, hasJobAlert: boolean) => void;
}

type formValues = {
  query: string;
  location: string;
  radius: number;
  customRequirements: string;
};

const JobRecommendationDrawer: FC<JobRecommendationDrawerProps> = ({
  drawerOpened,
  drawerHandlers,
  chatSessionId,
  jobRecommendTaskStatusDto,
  setJobRecommendTaskStatusDTO,
  chatForm,
  updateHasJobAlert,
}) => {
  const [jobs, setJobs] = useState<AiSelectedJob[] | null>(null);
  const [resultText, setResultText] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [resultSuccessful, setResultSuccessful] = useState<boolean>(false);
  const [failedReason, setFailedReason] = useState<string | null>(null);
  const [jobRecommendTaskId, setJobRecommendTaskId] = useState<string | null>(
    null,
  );
  const [taskStatus, setTaskStatus] = useState<AiTaskStatus | null>(null);
  const [sentOrLikedIsLoading, setSentOrLikedIsLoading] = useState(false);
  const [jobQuery, setJobQuery] = useState<JobQuery | null>(null);

  const [modalOpened, modalHandlers] = useDisclosure(false);

  const theme = useMantineTheme();

  const pasteJobToMessageField = async (job: AiSelectedJob) => {
    chatForm.setFieldValue(
      "message",
      `https://www.indeed.com/viewjob?jk=${job.jobKey}`,
    );
  };

  const markJobAsLikedOrDisliked = async (
    job: AiSelectedJob,
    jobRecommendTaskId: string | null,
    userLiked: boolean,
  ) => {
    setSentOrLikedIsLoading(true);
    const data = {
      userLiked: userLiked,
    };

    const response = await fetchWithAuthentication(
      `/job_recommend/user_liked/${jobRecommendTaskId}/${job.jobKey}`,
      "PATCH",
      data,
    );

    if (response.ok) {
      job.userLiked = userLiked;
    } else {
      setResultSuccessful(false);
      setResultText(
        "Something went wrong while trying to mark this job's liked status, please try again.",
      );
      setShowToast(true);
    }

    setSentOrLikedIsLoading(false);
  };

  const jobRecommendationForm = useForm<formValues>({
    mode: "uncontrolled",
    initialValues: {
      query: "",
      location: "",
      radius: 5,
      customRequirements: "",
    },
    validate: {
      query: (query: string) =>
        query.trim().length === 0
          ? "Query cannot be empty."
          : query.trim().length >= 200
            ? "Query must be fewer than 200 characters."
            : null,
      location: (location: string) =>
        location.trim().length === 0
          ? "Location cannot be empty."
          : location.trim().length >= 100
            ? "Location must be fewer than 100 characters."
            : null,
      radius: (radius: number) =>
        radius < 5
          ? "Radius must be at least 5 miles."
          : radius > 100
            ? "Radius must be fewer than 100 miles."
            : null,
      customRequirements: (customRequirements: string) =>
        customRequirements.length > 1000
          ? "Total number of characters for custom requirements must be fewer than 1000 characters."
          : null,
    },
  });

  const getJobRecommendationJobs = useCallback(async () => {
    const response = await fetchWithAuthentication(
      `/job_recommend/${chatSessionId}`,
      "GET",
      null,
    );
    if (response.ok) {
      const json: AiJobRecommendResultResponse = await response.json();
      setJobs(json.selectedJobs);
      setJobRecommendTaskId(json.jobRecommendTaskId);
      setResultSuccessful(true);
      const jobQueryValues = {
        jobTitle: json.jobTitle,
        query: json.jobTitle,
        location: json.location,
        radius: json.radius,
        customRequirements: json.customRequirements,
      };
      setJobQuery(jobQueryValues);
      jobRecommendationForm.setValues(jobQueryValues);
      setTaskStatus(json.taskStatus);
      setFailedReason(json.failedReason);
    } else if (response.status === 404) {
    } else {
      setResultSuccessful(false);
      setResultText(
        "Something went wrong when fetching the job list, please try again.",
      );
      setShowToast(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionId]);

  const postJobRecommendationRequest = async () => {
    const data = {
      query: jobRecommendationForm.getValues().query,
      location: jobRecommendationForm.getValues().location,
      radius: jobRecommendationForm.getValues().radius,
      customRequirements: jobRecommendationForm.getValues().customRequirements,
    };
    const response = await fetchWithAuthentication(
      `/job_recommend/${chatSessionId}`,
      "POST",
      data,
    );
    if (response.ok) {
      const json = await response.json();
      setResultText(
        "Request sent successfully. This drawer will automatically update with the selected jobs once the AI task has completed.",
      );
      setResultSuccessful(true);
      setShowToast(true);
      setJobRecommendTaskStatusDTO({
        status: AiTaskStatus.RUNNING,
        jobRecommendTaskId: json.jobRecommendTaskId,
        jobTitle: jobRecommendationForm.getValues().query,
        location: jobRecommendationForm.getValues().location,
        chatSessionId: chatSessionId!,
      });
      // Fetch job recommendations after successfully creating the task
      getJobRecommendationJobs();
    } else {
      setResultText(
        "Something went wrong when trying to send a job recommendation request, please try again.",
      );
      setResultSuccessful(false);
      setShowToast(true);
    }
    jobRecommendationForm.reset();
  };

  useEffect(() => {
    if (drawerOpened) {
      getJobRecommendationJobs();
    }
  }, [jobRecommendTaskStatusDto, drawerOpened, getJobRecommendationJobs]);

  useEffect(() => {
    setJobRecommendTaskId(null);
    setJobs(null);
    setJobQuery(null);
    jobRecommendationForm.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSessionId]);

  return (
    <>
      {showToast && (
        <Box
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
          }}
        >
          <Notification
            onClose={() => setShowToast(false)}
            title={resultSuccessful ? "Success" : "Error"}
            icon={resultSuccessful ? <MdCheck /> : <MdClose />}
            color={
              resultSuccessful ? theme.colors.green[5] : theme.colors.red[5]
            }
          >
            {resultText}
          </Notification>
        </Box>
      )}

      <HistoricalJobRecommendationsModal
        modalOpened={modalOpened}
        modalHandlers={modalHandlers}
        chatSessionId={chatSessionId}
        sentOrLikedIsLoading={sentOrLikedIsLoading}
        markJobAsSentToUser={pasteJobToMessageField}
        markJobAsLikedOrDisliked={markJobAsLikedOrDisliked}
        updateHasJobAlert={updateHasJobAlert}
      />

      <Drawer
        opened={drawerOpened}
        onClose={drawerHandlers.close}
        title="Job recommendation"
      >
        {jobs == null && (
          <form
            onSubmit={jobRecommendationForm.onSubmit(
              postJobRecommendationRequest,
            )}
          >
            <Stack>
              <TextInput
                label="Job title"
                description="The job title and optionally seniority"
                placeholder="Senior Software Engineer"
                required
                {...jobRecommendationForm.getInputProps("query")}
                key={jobRecommendationForm.key("query")}
              />
              <TextInput
                label="Location"
                description="City and state abbreviation, or 'remote'"
                placeholder="Houston, TX"
                required
                {...jobRecommendationForm.getInputProps("location")}
                key={jobRecommendationForm.key("location")}
              />
              <NumberInput
                label="Radius"
                description="In miles, between 5 and 100"
                required
                {...jobRecommendationForm.getInputProps("radius")}
                key={jobRecommendationForm.key("radius")}
                min={5}
                max={100}
              />
              <Textarea
                label="Custom requirements"
                description="Type in a requirement and press enter to add it to the list"
                {...jobRecommendationForm.getInputProps("customRequirements")}
                key={jobRecommendationForm.key("customRequirements")}
                resize="vertical"
                autosize
                minRows={3}
              />
              <Button type="submit">Submit</Button>
            </Stack>
          </form>
        )}
        {jobs && (
          <Stack>
            <Button
              onClick={() => setJobs(null)}
              disabled={
                taskStatus != null &&
                taskStatus !== AiTaskStatus.DONE &&
                taskStatus !== AiTaskStatus.OOPS
              }
            >
              Go to job request form
            </Button>
            <Button
              onClick={() => {
                modalHandlers.open();
                drawerHandlers.close();
              }}
            >
              Historical job recommendations
            </Button>
            <Title order={5}>Job search Conditions:</Title>
            <Text fw={600}>
              Job title: <Text span>{jobQuery?.jobTitle}</Text>
            </Text>
            <Text fw={600}>
              Location: <Text span>{jobQuery?.location}</Text>
            </Text>
            <Text fw={600}>
              Radius: <Text span>{jobQuery?.radius}</Text>
            </Text>
            <Text fw={600}>
              Custom requirements:{" "}
              <Text span style={{ whiteSpace: "pre-wrap" }}>
                {jobQuery?.customRequirements
                  ? jobQuery.customRequirements
                  : "None"}
              </Text>
            </Text>

            <Divider />

            {taskStatus !== AiTaskStatus.DONE &&
              taskStatus !== AiTaskStatus.OOPS && (
                <Center>
                  <Stack align="center" gap="xs">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                      Loading job recommendations...
                    </Text>
                  </Stack>
                </Center>
              )}

            {taskStatus === AiTaskStatus.DONE && jobs.length === 0 && (
              <Text>
                The AI successfully performed its task but did not find any jobs
                that meet the cutoff score of 60.
              </Text>
            )}
            {taskStatus !== AiTaskStatus.DONE &&
              taskStatus !== AiTaskStatus.OOPS && (
                <Text>
                  The job recommend task is still running. Please wait a moment.
                </Text>
              )}
            {taskStatus === AiTaskStatus.OOPS && (
              <Text>The job recommend task failed. {failedReason}</Text>
            )}
            {jobs.map((job, index) => (
              <Paper withBorder p="md" key={job.jobKey + index}>
                <Stack>
                  <Title order={3}>{job.jobTitle}</Title>
                  <Text fw={600}>{job.location}</Text>
                  <Text fw={600}>
                    AI score: <Text span>{job.score}</Text>
                  </Text>
                  <Text fw={600}>
                    Reasons: <Text span>{job.reasons}</Text>
                  </Text>
                  <Text fw={600}>
                    Job URL:{" "}
                    <Anchor
                      href={`https://www.indeed.com/viewjob?jk=${job.jobKey}`}
                      target="_blank"
                    >
                      {`https://www.indeed.com/viewjob?jk=${job.jobKey}`}
                    </Anchor>
                  </Text>
                  {sentOrLikedIsLoading && (
                    <Center>
                      <Loader />
                    </Center>
                  )}
                  {!job.sentToChat && !sentOrLikedIsLoading && (
                    <>
                      <Button onClick={() => pasteJobToMessageField(job)}>
                        Send link to chat
                      </Button>
                      <Text fw={300} size="sm">
                        Clicking this button will copy the job into the chat
                        message field. Once you send the message it will become
                        marked as being sent to the user.
                      </Text>
                    </>
                  )}
                  {job.sentToChat &&
                    job.userLiked == null &&
                    !sentOrLikedIsLoading && (
                      <Group grow>
                        <Button
                          onClick={() =>
                            markJobAsLikedOrDisliked(
                              job,
                              jobRecommendTaskId,
                              true,
                            )
                          }
                        >
                          User liked
                        </Button>
                        <Button
                          onClick={() =>
                            markJobAsLikedOrDisliked(
                              job,
                              jobRecommendTaskId,
                              false,
                            )
                          }
                          color={theme.colors.red[5]}
                        >
                          User Disliked
                        </Button>
                      </Group>
                    )}
                  {job.userLiked != null && !sentOrLikedIsLoading && (
                    <Paper
                      h="34px"
                      bg={
                        job.userLiked
                          ? theme.colors.blue[5]
                          : theme.colors.red[5]
                      }
                    >
                      <Center h="100%">
                        <Text c="white">
                          {job.userLiked ? "Liked" : "Disliked"}
                        </Text>
                      </Center>
                    </Paper>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Drawer>
    </>
  );
};

export default JobRecommendationDrawer;
